import { useEffect } from 'react';
import { convertToMomentJs } from '@/shared/lib/aiUtils';
import { BEAT_HR_TAB_ID } from '@/entities/report';
import type { NavigatorContainerProps } from './navigatorContainer';

/**
 * Computes the strip navigator's clock labels and disabled arrows for the RR-interval beat tab. Derives the displayed strip moment with its five-second bounds, disables stepping past the report start or stop or into a range with no ECG data, and binds the comma and period keys to step the strip while the pointer hovers the beat tab.
 *
 * @param props - The navigator inputs carrying the report start and stop, timezone offset, displayed beat id, no-data range flags and the navigate callback.
 * @returns The navigator state: the previous and next disabled flags plus the centre, minus-five and plus-five second moments.
 */
const useNavigatorContainer = ({
  startReport,
  stopReport,
  timezoneOffset,
  displayedBeatId,
  disabledNoDataRange,
  navigateStrip,
}: NavigatorContainerProps) => {
  const startReportMoment = convertToMomentJs(startReport, timezoneOffset);
  const stopReportMoment = convertToMomentJs(stopReport, timezoneOffset);
  const selectedStripMoment = convertToMomentJs(displayedBeatId, timezoneOffset);
  const selectedStripSubtractMoment = selectedStripMoment?.clone()?.subtract(5, 'seconds');
  const selectedStripAddMoment = selectedStripMoment?.clone()?.add(5, 'seconds');
  const disabledPrevious =
    selectedStripMoment?.clone()?.subtract(10, 'seconds').valueOf() < startReportMoment.valueOf();
  const disabledNext =
    selectedStripMoment.clone().add(10, 'seconds').valueOf() > stopReportMoment.valueOf();
  const isDisabledPrevious =
    !displayedBeatId || disabledPrevious || disabledNoDataRange.isDisabledPrev;
  const isDisabledNext = !displayedBeatId || disabledNext || disabledNoDataRange.isDisabledNext;

  useEffect(() => {
    /**
     * Steps the displayed strip backward or forward when comma or period is pressed over the beat tab. Ignores auto-repeat, presses made while the pointer is outside the beat tab element, and any press carrying control or meta, then calls the navigate callback only in the direction that is not disabled.
     *
     * @param event - The keydown event, inspected for repeat, key code and modifier keys.
     * @returns Nothing.
     */
    const handleKeyDown = (event: any) => {
      if (event?.repeat) {
        return;
      }
      const tabZone = document.getElementById(BEAT_HR_TAB_ID);
      // *:Active key press when hover in tab zone
      if (tabZone && !tabZone.matches(':hover')) {
        return;
      }
      const { keyCode, ctrlKey, metaKey } = event;
      if (ctrlKey || metaKey) {
        return;
      }
      switch (keyCode) {
        case 188: {
          // ,<
          if (!isDisabledPrevious) {
            navigateStrip();
          }
          break;
        }
        case 190: {
          // .>
          if (!isDisabledNext) {
            navigateStrip(true);
          }
          break;
        }
        default: {
          break;
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isDisabledPrevious, isDisabledNext, navigateStrip]);

  return {
    isDisabledPrevious,
    isDisabledNext,
    selectedStripMoment,
    selectedStripSubtractMoment,
    selectedStripAddMoment,
  };
};

export default useNavigatorContainer;
